/**
 * Pure helpers of the signature screens (spec 2026-10-05-05), kept out of the
 * components so they are unit-tested.
 */

/** A drawn signature: strokes of [x, y] points normalized to the frame. */
export type Strokes = Array<Array<[number, number]>>

/** Mirrors internal/signing: at least two real strokes, a third of the frame long. */
export const MIN_STROKES = 2
export const MIN_PATH_LENGTH = 0.3
/** The drawing frame's aspect ratio (600 x 200 on the server). */
export const FRAME_RATIO = 3

const round = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 10000) / 10000

/**
 * The normalized point of a pointer position inside the frame. Only the
 * geometry is kept: never the time nor the pressure of the point (that would
 * be biometric data).
 */
export function normalizePoint(clientX: number, clientY: number, rect: { left: number; top: number; width: number; height: number }): [number, number] {
    return [round((clientX - rect.left) / rect.width), round((clientY - rect.top) / rect.height)]
}

/** Total path length, the y axis scaled to the frame's ratio like the server. */
export function pathLength(strokes: Strokes): number {
    let length = 0
    for (const s of strokes) {
        for (let i = 1; i < s.length; i++) {
            length += Math.hypot(s[i][0] - s[i - 1][0], (s[i][1] - s[i - 1][1]) / FRAME_RATIO)
        }
    }
    return length
}

/** Whether the server will accept the drawing. */
export function isDrawingAcceptable(strokes: Strokes): boolean {
    const real = strokes.filter((s) => s.length >= 2).length
    return real >= MIN_STROKES && pathLength(strokes) >= MIN_PATH_LENGTH
}

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'info' | 'outline'

/** The badge of a request status. */
export function requestStatusVariant(status: string): BadgeVariant {
    switch (status) {
        case 'completed':
            return 'success'
        case 'pending':
            return 'info'
        case 'declined':
            return 'destructive'
        case 'expired':
        case 'cancelled':
            return 'secondary'
        default:
            return 'outline'
    }
}

/** The badge of a signer status. */
export function signerStatusVariant(status: string): BadgeVariant {
    switch (status) {
        case 'signed':
            return 'success'
        case 'pending':
            return 'info'
        case 'declined':
            return 'destructive'
        default:
            return 'secondary'
    }
}

/** Reads the link token from the URL fragment (#t=...), "" when absent. */
export function tokenFromHash(hash: string): string {
    const params = new URLSearchParams(hash.replace(/^#/, ''))
    return params.get('t') ?? ''
}

/** The handwritten signature as the sign endpoint takes it. */
export type Handwriting =
    | { kind: 'drawn'; strokes: Strokes }
    | { kind: 'typed'; typed: string }

/** Whether the handwriting can be sent. */
export function isHandwritingReady(h: Handwriting | null): boolean {
    if (!h) return false
    return h.kind === 'drawn' ? isDrawingAcceptable(h.strokes) : h.typed.trim().length > 0
}
